<template>
  <div class="Scene">
    <canvas
      id="renderCanvas"
      ref="canvas"
      class="Scene-canvas"
      touch-action="none"
      oncontextmenu="return false"
    ></canvas>
  </div>
</template>

<script>
import {
  Engine,
  Scene,
  ArcRotateCamera,
  Vector2,
  Vector3,
  HemisphericLight,
  PointLight,
  Tools,
  HighlightLayer,
  Color3,
  Color4,
  PointerDragBehavior,
  AutoRotationBehavior,
  Action,
  Matrix,
  Mesh,
} from "@babylonjs/core";

import "@babylonjs/loaders/OBJ";
import "@babylonjs/loaders/STL";
import { AssetsManager } from "@babylonjs/core/Misc/assetsManager";
import { AdvancedDynamicTexture, Button, TextBlock } from "@babylonjs/gui/2D";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { Control } from "@babylonjs/gui/2D/controls/control";
import { ActionManager } from "@babylonjs/core/Actions/actionManager";
import { ExecuteCodeAction } from "@babylonjs/core/Actions/directActions";
import { DefaultRenderingPipeline } from "@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline";
import {
  loadPlateMesh,
  loadSymbolAMesh,
  loadSymbolBMesh,
  createCylinder,
} from "./lib/assets";
import { createPlateMaterial, createCylinderMaterial } from "./lib/materials";

import { StatusEnum, xRight, xLeft } from "../shared/constants";
import { mapActions, mapGetters } from "vuex";
import { EventBus } from "../../main";

export default {
  name: "Scene",
  components: {},
  provide() {
    return {
      babylon: this.babylon,
    };
  },
  computed: {
    ...mapGetters([
      "settings",
      "allCylinders",
      "leftCylinders",
      "rightCylinders",
    ]),
  },
  created() {
    window.addEventListener("resize", () => this.layout());
  },

  data() {
    return {
      logik: {
        selectedCylinder: null,
        leftCylinders: null,
        rightCylinders: null,
        currentStatus: StatusEnum.NONE,
      },
      babylon: {
        scene: null,
        sceneReady: false,
        canvas: null,
      },
      scene: {
        camera: null,
        plateMesh: null,
        symbolAMesh: null,
        symbolBMesh: null,
        cylinderMeshes: new Map(),
        plateMaterial: null,
        cylinderMaterial: null,
        highlightLayer: null,
      },
      gui: {
        leftPanel: null,
        bottomPanel: null,
        colorButtons: new Map(),
        deleteCylinderButton: null,
      },
    };
  },
  async mounted() {
    await this.createScene();
    this.logik.leftCylinders = [];
    this.logik.rightCylinders = [];
    this.logik.leftCylinders.height = 0;
    this.logik.rightCylinders.height = 0;

    if (this.allCylinders.length == 0) {
      this.$store.commit("updateRightCylinders", this.logik.rightCylinders);
      this.$store.commit("updateLeftCylinders", this.logik.leftCylinders);
    } else {
      this.restoreScene();
    }
    this.updateSymbolHeights();
    this.babylon.sceneReady = true;
    this.babylon.canvas = this.$refs.canvas;

    this.engine.runRenderLoop(() => {
      this.babylon.scene.render();
    });

    EventBus.$on("onSuccessfulOrder", () => {
      this.resetConfigurator();
    });
  },
  beforeDestroy() {
    this.babylon.scene.dispose();
    this.engine.dispose();
  },
  methods: {
    async createScene() {
      // Create render loop, a camera and some basic lights:
      this.engine = new Engine(
        this.$refs.canvas,
        true,
        { preserveDrawingBuffer: true },
        false
      );
      this.babylon.scene = new Scene(this.engine);
      var camera = new ArcRotateCamera(
        "camera",
        -Math.PI / 2,
        Tools.ToRadians(67),
        300,
        new Vector3(0, 50, 0),
        this.babylon.scene
      );
      this.scene.camera = camera;
      camera.useAutoRotationBehavior = true;
      camera.autoRotationBehavior.idleRotationWaitTime = 10000;
      camera.autoRotationBehavior.idleRotationSpeed = 0.25;
      camera.lowerRadiusLimit = 250;
      camera.upperRadiusLimit = 450;

      camera.attachControl(this.$refs.canvas, true);
      this.scene.highlightLayer = new HighlightLayer("hl1", this.babylon.scene);
      new HemisphericLight("light1", new Vector3(1, 1, 0), this.babylon.scene);

      this.babylon.scene.createDefaultEnvironment({
        createGround: false,
        createSkybox: true,
        skyboxSize: 1000,
        skyboxColor: new Color3(144 / 255, 202 / 255, 249 / 255),
        enableGroundMirror: false,
      });

      var defaultPipeline = new DefaultRenderingPipeline(
        "DefaultRenderingPipeline",
        false, // is HDR?
        this.babylon.scene,
        this.babylon.scene.cameras
      );
      defaultPipeline.samples = 1;
      defaultPipeline.fxaaEnabled = false;

      this.scene.plateMaterial = createPlateMaterial(this.babylon.scene);
      this.scene.cylinderMaterial = createCylinderMaterial(this.babylon.scene);
      var assetLoader = new AssetsManager(this.babylon.scene);
      loadPlateMesh(
        assetLoader,
        (mesh) => (this.scene.plateMesh = mesh),
        this.scene.plateMaterial
      );
      loadSymbolAMesh(assetLoader, (mesh) => {
        this.scene.symbolAMesh = mesh;
        this.scene.symbolAMesh.billboardMode = Mesh.BILLBOARDMODE_ALL;
      });
      loadSymbolBMesh(assetLoader, (mesh) => {
        this.scene.symbolBMesh = mesh;
        this.scene.symbolBMesh.billboardMode = Mesh.BILLBOARDMODE_ALL;
      });
      await assetLoader.loadAsync();
      this.createCylinderMeshes(this.settings.cylinders);

      var advancedTexture = AdvancedDynamicTexture.CreateFullscreenUI("gui");
      var leftPanel = new StackPanel();
      leftPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
      leftPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      leftPanel.isVertical = true;
      leftPanel.adaptWidthToChildren = true;
      advancedTexture.addControl(leftPanel);
      this.addHeader(leftPanel);
      this.addCylinderButtons(this.settings.cylinders, leftPanel);
      this.gui.leftPanel = leftPanel;
      leftPanel.left = "25px";

      var bottomPanel = new StackPanel();
      bottomPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
      bottomPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
      bottomPanel.isVertical = false;
      bottomPanel.adaptHeightToChildren = true;
      bottomPanel.isVisible = false;
      bottomPanel.top = -20;
      advancedTexture.addControl(bottomPanel);
      this.gui.bottomPanel = bottomPanel;

      this.addDeleteCylinderButton(bottomPanel);
      this.addColorButtons(this.settings.cylinders, bottomPanel);
    },

    restoreScene() {
      var i;
      var currentCylinder;
      for (i = 0; i < this.leftCylinders.length; i++) {
        currentCylinder = this.leftCylinders[i];

        this.spawnCylinder(currentCylinder.height, false);
        this.logik.selectedCylinder.material.baseColor =
          currentCylinder.material.baseColor;

        this.logik.selectedCylinder.color = currentCylinder.color;
        this.logik.selectedCylinder.position = currentCylinder.position;
        this.logik.selectedCylinder.stack = currentCylinder.stack;
        this.registerOnClickAction(this.logik.selectedCylinder);

        this.logik.leftCylinders.push(this.logik.selectedCylinder);
        this.logik.leftCylinders.height += currentCylinder.height;
      }
      for (i = 0; i < this.rightCylinders.length; i++) {
        currentCylinder = this.rightCylinders[i];

        this.spawnCylinder(currentCylinder.height, false);
        this.logik.selectedCylinder.material.baseColor =
          currentCylinder.material.baseColor;
        this.logik.selectedCylinder.color = currentCylinder.color;
        this.logik.selectedCylinder.position = currentCylinder.position;
        this.logik.selectedCylinder.stack = currentCylinder.stack;
        this.registerOnClickAction(this.logik.selectedCylinder);

        this.logik.rightCylinders.push(this.logik.selectedCylinder);
        this.logik.rightCylinders.height += currentCylinder.height;
      }
      this.$store.commit("updateRightCylinders", this.logik.rightCylinders);
      this.$store.commit("updateLeftCylinders", this.logik.leftCylinders);
    },

    createCylinderMeshes(cylinders) {
      cylinders.forEach((cylinder) => {
        var mesh = createCylinder(
          cylinder.height,
          cylinder.colors[0],
          this.scene.cylinderMaterial.clone(),
          this.babylon.scene
        );
        mesh.config = cylinder;
        mesh.isVisible = false;
        this.scene.cylinderMeshes.set(cylinder.height, mesh);
      });
    },

    spawnCylinder(height, addDragBehavior) {
      var baseMesh = this.scene.cylinderMeshes.get(height);
      this.logik.selectedCylinder = baseMesh.clone();
      this.logik.selectedCylinder.material = baseMesh.material.clone();
      this.logik.selectedCylinder.config = baseMesh.config;
      this.logik.selectedCylinder.color = this.logik.selectedCylinder.config.colors[0];
      this.logik.selectedCylinder.isVisible = true;

      this.logik.selectedCylinder.position = new Vector3(
        0,
        this.settings.maxHeight + this.logik.selectedCylinder.height / 2,
        0
      );
      if (addDragBehavior) {
        this.logik.selectedCylinder.addBehavior(
          this.createCylinderDragBehavior()
        );
      }
    },

    createCylinderDragBehavior() {
      var pointerDragBehavior = new PointerDragBehavior({
        dragPlaneNormal: new Vector3(0, 0, 1),
      });

      pointerDragBehavior.onDragStartObservable.add((event) => {});
      pointerDragBehavior.onDragObservable.add((event) => {
        this.scene.highlightLayer.removeMesh(pointerDragBehavior.attachedNode);
        this.snapOnDemand(pointerDragBehavior.attachedNode);
      });
      pointerDragBehavior.onDragEndObservable.add((event) => {
        this.snapOnDemand(pointerDragBehavior.attachedNode);
        if (this.placeCylinderOnDemand(pointerDragBehavior.attachedNode)) {
          this.$store.commit("updateRightCylinders", this.logik.rightCylinders);
          this.$store.commit("updateLeftCylinders", this.logik.leftCylinders);

          this.registerOnClickAction(pointerDragBehavior.attachedNode);
          this.logik.currentStatus = StatusEnum.NONE;
          this.logik.selectedCylinder = null;

          pointerDragBehavior.enabled = false;
          this.gui.leftPanel.isVisible = true;
          this.changeBottomPanelVisibility(null, false);
          this.scene.highlightLayer.removeAllMeshes();
        }
      });
      pointerDragBehavior.validateDrag = (targetPosition) => {
        return (
          targetPosition.y >= 0 &&
          targetPosition.y <= this.settings.maxHeight * 2 &&
          targetPosition.x > xLeft - 200 &&
          targetPosition.x < xRight + 200
        );
      };

      return pointerDragBehavior;
    },

    snapOnDemand(cylinder) {
      var x = cylinder.position.x;
      var y = cylinder.position.y;
      var leftDistance = Vector2.Distance(
        new Vector2(x, y),
        new Vector2(xLeft, this.logik.leftCylinders.height)
      );
      var rightDistance = Vector2.Distance(
        new Vector2(x, y),
        new Vector2(xRight, this.logik.rightCylinders.height)
      );

      if (this.canPlaceLeft(cylinder, 20)) {
        cylinder.position = new Vector3(
          xLeft,
          this.logik.leftCylinders.height + cylinder.height / 2,
          0
        );
        this.scene.highlightLayer.addMesh(cylinder, Color3.Green());
      } else if (this.canPlaceRight(cylinder, 10)) {
        cylinder.position = new Vector3(
          xRight,
          this.logik.rightCylinders.height + cylinder.height / 2,
          0
        );
        this.scene.highlightLayer.addMesh(cylinder, Color3.Green());
      } else {
        this.scene.highlightLayer.addMesh(cylinder, Color3.Red());
      }
    },

    canPlaceLeft(cylinder, distanceThreshold) {
      var x = cylinder.position.x;
      var y = cylinder.position.y;
      y -= cylinder.height / 2;

      if (this.logik.leftCylinders.length == this.settings.maxCylinders) {
        return false;
      }

      if (
        this.logik.leftCylinders.height + cylinder.height >
        this.settings.maxHeight
      ) {
        return false;
      }

      var distance = Vector2.Distance(
        new Vector2(x, y),
        new Vector2(xLeft, this.logik.leftCylinders.height)
      );
      if (distance > distanceThreshold) {
        return false;
      }

      return true;
    },

    canPlaceRight(cylinder, distanceThreshold) {
      var x = cylinder.position.x;
      var y = cylinder.position.y;
      y -= cylinder.height / 2;

      if (this.logik.rightCylinders.length == this.settings.maxCylinders) {
        return false;
      }

      if (
        this.logik.rightCylinders.height + cylinder.height >
        this.settings.maxHeight
      ) {
        return false;
      }

      var distance = Vector2.Distance(
        new Vector2(x, y),
        new Vector2(xRight, this.logik.rightCylinders.height)
      );
      if (distance > distanceThreshold) {
        return false;
      }

      return true;
    },

    placeCylinderOnDemand(cylinder) {
      var x = cylinder.position.x;
      var y = cylinder.position.y;

      if (this.canPlaceLeft(cylinder, 0)) {
        this.logik.leftCylinders.push(cylinder);
        this.logik.leftCylinders.height += cylinder.height;
        cylinder.stack = 0;
        this.updateSymbolHeights();
        return true;
      } else if (this.canPlaceRight(cylinder, 0)) {
        this.logik.rightCylinders.push(cylinder);
        this.logik.rightCylinders.height += cylinder.height;
        cylinder.stack = 1;
        this.updateSymbolHeights();
        return true;
      }
      return false;
    },

    registerOnClickAction(cylinder) {
      cylinder.actionManager = new ActionManager(this.babylon.scene);
      cylinder.actionManager.registerAction(
        new ExecuteCodeAction(
          {
            trigger: ActionManager.OnPickDownTrigger,
          },
          (evt) => {
            if (this.logik.currentStatus != StatusEnum.PLACING_CYLINDER) {
              this.scene.highlightLayer.removeAllMeshes();
              if (evt.source != this.logik.selectedCylinder) {
                this.scene.highlightLayer.addMesh(evt.source, Color3.Green());
                this.logik.selectedCylinder = evt.source;
                this.changeBottomPanelVisibility(evt.source, true);
                this.logik.currentStatus = StatusEnum.CYLINDER_SELECTED;
              } else {
                this.logik.selectedCylinder = null;
                this.changeBottomPanelVisibility(null, false);
                this.logik.currentStatus = StatusEnum.NONE;
              }
            }
          }
        )
      );
    },

    addColorButtons(cylinders, panel) {
      cylinders.forEach((cylinder) => {
        cylinder.colors.forEach((color) => {
          var button = this.gui.colorButtons.get(color.name);
          if (button == null) {
            button = Button.CreateSimpleButton("color_" + color.name, "");
            button.width = "60px";
            button.height = "50px";
            button.color = "white";
            button.cornerRadius = 50;
            button.paddingLeft = 10;
            button.background = new Color3(
              color.r,
              color.g,
              color.b
            ).toHexString();
            button.isVisible = true;
            button.onPointerClickObservable.add(() => {
              this.changeColor(color);
              this.logik.selectedCylinder.color = color;
            });
            this.gui.colorButtons.set(color.name, button);
            panel.addControl(button);
          }
        });
      });
    },
    addHeader(panel) {
      var header = new TextBlock();
      header.text = "Zylinder hinzufügen";
      header.color = "white";
      header.width = "160px";
      header.height = "50px";
      header.fontFamily = "Arial";
      header.fontSize = 16;
      panel.addControl(header);
    },
    addCylinderButtons(cylinders, panel) {
      cylinders.forEach((cylinder) => {
        var button = Button.CreateSimpleButton(
          "addCylinder_" + cylinder.height,
          cylinder.height + " mm"
        );
        button.width = "140px";
        button.height = "50px";
        button.color = "white";
        button.fontFamily = "Arial";
        button.background = "#6c757d";
        button.cornerRadius = 5;
        button.paddingBottom = 15;
        button.fontSize = 14;

        button.onPointerClickObservable.add(() => {
          this.spawnCylinder(cylinder.height, true);
          this.scene.highlightLayer.removeAllMeshes();
          this.scene.highlightLayer.addMesh(
            this.logik.selectedCylinder,
            Color3.Red()
          );
          this.gui.leftPanel.isVisible = false;
          this.changeBottomPanelVisibility(this.logik.selectedCylinder, true);
          this.logik.currentStatus = StatusEnum.PLACING_CYLINDER;
        });
        panel.addControl(button);
      });
    },
    addDeleteCylinderButton(panel) {
      var button = Button.CreateSimpleButton("deleteCylinder", "\uf014");
      button.width = "80px";
      button.height = "50px";
      button.color = "white";
      button.fontSize = 26;
      button.fontWeight = 300;
      button.fontFamily = "FontAwesome";
      button.background = "#6c757d";
      button.cornerRadius = 5;
      button.thickness = 1;

      button.paddingRight = "30px";
      button.isVisible = true;

      button.onPointerClickObservable.add(this.createDeleteCylinderListener());
      panel.addControl(button);
      this.gui.deleteCylinderButton = button;
    },

    createDeleteCylinderListener() {
      return () => {
        if (this.logik.currentStatus == StatusEnum.CYLINDER_SELECTED) {
          var cylinderStack;
          if (this.logik.selectedCylinder.stack == 0) {
            cylinderStack = this.logik.leftCylinders;
          } else {
            cylinderStack = this.logik.rightCylinders;
          }

          const index = cylinderStack.indexOf(this.logik.selectedCylinder);

          if (index > -1) {
            cylinderStack.height -= this.logik.selectedCylinder.height;
            for (var i = index + 1; i < cylinderStack.length; i++) {
              cylinderStack[i].position.y -= this.logik.selectedCylinder.height;
            }
            cylinderStack.splice(index, 1);
            this.logik.selectedCylinder.dispose();
            this.logik.selectedCylinder = null;
            this.changeBottomPanelVisibility(null, false);
            this.updateSymbolHeights();
          } else {
            return;
          }
        } else if (this.logik.currentStatus == StatusEnum.PLACING_CYLINDER) {
          this.logik.selectedCylinder.dispose();
          this.logik.currentStatus = StatusEnum.NONE;
          this.changeBottomPanelVisibility(null, false);
          this.gui.leftPanel.isVisible = true;
        }
      };
    },

    changeColor(color) {
      if (this.logik.selectedCylinder) {
        this.logik.selectedCylinder.material.baseColor = new Color3(
          color.r,
          color.g,
          color.b
        );
        this.$store.commit("updateRightCylinders", this.logik.rightCylinders);
        this.$store.commit("updateLeftCylinders", this.logik.leftCylinders);
      }
    },

    changeBottomPanelVisibility(cylinder, isVisible) {
      if (isVisible) {
        this.gui.colorButtons.forEach((button) => {
          button.isVisible = false;
        });

        cylinder.config.colors.forEach((color) => {
          var button = this.gui.colorButtons.get(color.name);
          if (button != null) {
            button.isVisible = true;
          }
        });
      } else {
        this.gui.colorButtons.forEach((button) => {
          button.isVisible = false;
        });
      }
      this.gui.deleteCylinderButton.isVisible = isVisible;
      this.gui.bottomPanel.isVisible = isVisible;

      //Workaround für GUI-Bug, wenn Icons sich verschieben
      this.gui.deleteCylinderButton.thickness = 0;
      this.gui.deleteCylinderButton.thickness = 1;
    },

    updateSymbolHeights() {
      var maxHeight = Math.max(
        this.logik.leftCylinders.height,
        this.logik.rightCylinders.height
      );
      this.scene.symbolAMesh.position.y = this.scene.symbolBMesh.position.y =
        maxHeight + 70;
    },

    resetConfigurator() {
      if (this.logik.selectedCylinder) {
        this.logik.selectedCylinder.dispose();
      }
      this.logik.leftCylinders.forEach((cylinder) => {
        cylinder.dispose();
      });
      this.logik.rightCylinders.forEach((cylinder) => {
        cylinder.dispose();
      });

      this.logik.currentStatus = StatusEnum.NONE;

      this.logik.leftCylinders = [];
      this.logik.rightCylinders = [];
      this.logik.leftCylinders.height = 0;
      this.logik.rightCylinders.height = 0;
      this.$store.commit("updateRightCylinders", this.logik.rightCylinders);
      this.$store.commit("updateLeftCylinders", this.logik.leftCylinders);

      this.gui.leftPanel.isVisible = true;
      this.gui.bottomPanel.isVisible = false;
      this.updateSymbolHeights();
    },
    layout() {
      if (this.engine) this.engine.resize();
    },
  },
};
</script>

<style scoped>
.Scene {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  background-color: white;
  overflow: hidden;
}
.Scene-canvas {
  width: 100%;
  height: 100%;
  touch-action: none;
  outline: none;
  display: flex;
}
</style>
